import { useEffect, useState } from 'react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet.jsx';
import { Button } from '@/components/ui/button.jsx';
import { Input } from '@/components/ui/input.jsx';
import { Badge } from '@/components/ui/badge.jsx';
import { toast } from '@/components/ui/toaster.jsx';
import { Loader2 } from 'lucide-react';
import { getBrData, saveBrData } from '@/services/deals.service.js';
import ListSelect from '@/components/shared/ListSelect.jsx';

// BrDataSheet (BR-REPEAT-BIND Ship 2b) - the broker editor over the
// Builders_Risk_Data blob plus the deal-level terms (proposed effective,
// expiration, amount to collect, policy-number backfill). Built on the
// HangarApplicationSheet pattern: seed on open, edit locally, save via
// PUT /api/builders-risk/deal/:dealId/data (server validates and
// round-trips the stored blob).
//
// The bind-readiness strip keys on APPLICATION-DATA completeness (blob
// validity + project + premium + contact + carrier + proposed effective
// date), NEVER on Policy_Number. The policy number is a post-bind backfill
// field, assigned by the carrier at binding.
//
// Select options come from the server (GET response enums, canonical in
// utils/builders-risk-schema.js); the fallbacks below only cover a
// transiently missing enums payload.

const FALLBACK_ENUMS = {
  role_on_project: ['Owner', 'Contractor', 'Subcontractor', 'Owner and Contractor (same entity)', 'Other'],
  fire_department_types: ['Paid', 'Volunteer', 'Combination', 'None'],
  permission_to_occupy: ['Granted', 'Not Granted'],
  scaffolding_types: ['None', 'None, lifts only', 'Wood', 'Metal', 'Other'],
  single_or_multi: ['Single Project', 'Multiple Project'],
};

// Blob paths the server validator enforces as numbers. Everything else in
// the blob stays the string the broker typed (windstorm deductible is
// routinely "2% or $5,000" prose, coinsurance "80%", etc.).
const NUMERIC_COVERAGE_KEYS = [
  'any_jobsite_limit', 'any_jobsite_deductible', 'transit_limit', 'transit_deductible',
  'temp_storage_limit', 'temp_storage_deductible', 'any_one_loss_limit', 'any_one_loss_deductible',
  'earthquake_limit', 'flood_limit', 'annual_premium', 'tripra_premium',
];

const str = (v) => (v == null ? '' : String(v));
const boolToYN = (v) => (v === true ? 'Yes' : v === false ? 'No' : '');
const ynToBool = (v) => (v === 'Yes' ? true : v === 'No' ? false : null);
const numOrNull = (v) => {
  const s = String(v ?? '').trim();
  if (!s) return null;
  const n = Number(s.replace(/[$,]/g, ''));
  return Number.isFinite(n) ? n : null;
};

function seedForm(payload) {
  const d = payload?.data || {};
  const deal = payload?.deal || {};
  const applicant = d.applicant || {};
  const project = d.project || {};
  const addr = project.jobsite_address || {};
  const construction = d.construction || {};
  const protection = d.protection || {};
  const coverage = d.coverage || {};
  const tsr = d.transit_storage_rigging || {};
  const loss = d.loss_history || {};
  return {
    terms: {
      proposed_effective: str(deal.proposed_effective),
      expiration_date: str(deal.expiration_date),
      amount: str(deal.amount),
      policy_number: str(deal.policy_number),
    },
    applicant: {
      entity_type: str(applicant.entity_type),
      role_on_project: str(applicant.role_on_project),
      years_experience: str(applicant.years_experience),
      contractor_bonded: boolToYN(applicant.contractor_bonded),
      bonding_company: str(applicant.bonding_company),
      fein: str(applicant.fein),
    },
    project: {
      description: str(project.description),
      street: str(addr.street),
      city: str(addr.city),
      state: str(addr.state),
      zip: str(addr.zip),
      occupancy: str(project.occupancy),
      single_or_multi: str(project.single_or_multi),
      completed_value: str(project.completed_value),
      commencement_date: str(project.commencement_date),
      est_completion_date: str(project.est_completion_date),
    },
    construction: {
      foundation: str(construction.foundation),
      floors: str(construction.floors),
      walls: str(construction.walls),
      roof: str(construction.roof),
      stories_above: str(construction.stories_above),
      stories_below: str(construction.stories_below),
      dimensions: str(construction.dimensions),
      insulation: str(construction.insulation),
      percentage_glass: str(construction.percentage_glass),
    },
    protection: {
      town_protection_class: str(protection.town_protection_class),
      distance_to_hydrant: str(protection.distance_to_hydrant),
      distance_to_fire_station: str(protection.distance_to_fire_station),
      fire_department_type: str(protection.fire_department_type),
      fenced_lighted: boolToYN(protection.fenced_lighted),
      police_protection: boolToYN(protection.police_protection),
      standpipes: boolToYN(protection.standpipes),
      watchman_service: str(protection.watchman_service),
    },
    coverage: {
      ...Object.fromEntries(NUMERIC_COVERAGE_KEYS.map((k) => [k, str(coverage[k])])),
      windstorm_deductible: str(coverage.windstorm_deductible),
      coinsurance: str(coverage.coinsurance),
      permission_to_occupy: str(coverage.permission_to_occupy),
      tripra_accepted: boolToYN(coverage.tripra_accepted),
    },
    transit_storage_rigging: {
      transit_value: str(tsr.transit_value),
      off_site_temp_storage: str(tsr.off_site_temp_storage),
      rigging_hoisting: str(tsr.rigging_hoisting),
      scaffolding: str(tsr.scaffolding),
    },
    loss_history: {
      claims_summary: str(loss.claims_summary),
      current_prior_insurance: str(loss.current_prior_insurance),
    },
  };
}

// Rebuild the server blob shape from form state. Strings stay strings; the
// validator-enforced numeric paths convert (blank -> null). Yes/No toggles
// convert to real booleans (blank -> null).
function formToBlob(form) {
  const orNull = (v) => (String(v ?? '').trim() ? String(v).trim() : null);
  return {
    applicant: {
      entity_type: orNull(form.applicant.entity_type),
      role_on_project: orNull(form.applicant.role_on_project),
      years_experience: orNull(form.applicant.years_experience),
      contractor_bonded: ynToBool(form.applicant.contractor_bonded),
      bonding_company: orNull(form.applicant.bonding_company),
      fein: orNull(form.applicant.fein),
    },
    project: {
      description: orNull(form.project.description),
      jobsite_address: {
        street: orNull(form.project.street),
        city: orNull(form.project.city),
        state: orNull(form.project.state),
        zip: orNull(form.project.zip),
      },
      occupancy: orNull(form.project.occupancy),
      single_or_multi: orNull(form.project.single_or_multi),
      completed_value: numOrNull(form.project.completed_value),
      commencement_date: orNull(form.project.commencement_date),
      est_completion_date: orNull(form.project.est_completion_date),
    },
    construction: Object.fromEntries(
      Object.entries(form.construction).map(([k, v]) => [k, orNull(v)])
    ),
    protection: {
      town_protection_class: orNull(form.protection.town_protection_class),
      distance_to_hydrant: orNull(form.protection.distance_to_hydrant),
      distance_to_fire_station: orNull(form.protection.distance_to_fire_station),
      fire_department_type: orNull(form.protection.fire_department_type),
      fenced_lighted: ynToBool(form.protection.fenced_lighted),
      police_protection: ynToBool(form.protection.police_protection),
      standpipes: ynToBool(form.protection.standpipes),
      watchman_service: orNull(form.protection.watchman_service),
    },
    coverage: {
      ...Object.fromEntries(NUMERIC_COVERAGE_KEYS.map((k) => [k, numOrNull(form.coverage[k])])),
      windstorm_deductible: orNull(form.coverage.windstorm_deductible),
      coinsurance: orNull(form.coverage.coinsurance),
      permission_to_occupy: orNull(form.coverage.permission_to_occupy),
      tripra_accepted: ynToBool(form.coverage.tripra_accepted),
    },
    transit_storage_rigging: {
      transit_value: orNull(form.transit_storage_rigging.transit_value),
      off_site_temp_storage: orNull(form.transit_storage_rigging.off_site_temp_storage),
      rigging_hoisting: orNull(form.transit_storage_rigging.rigging_hoisting),
      scaffolding: orNull(form.transit_storage_rigging.scaffolding),
    },
    loss_history: {
      claims_summary: orNull(form.loss_history.claims_summary),
      current_prior_insurance: orNull(form.loss_history.current_prior_insurance),
    },
  };
}

function SectionTitle({ children }) {
  return <h3 className="text-[11px] font-semibold uppercase tracking-wide text-text-dim">{children}</h3>;
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="mb-1 block text-[11px] text-text-dim">{label}</span>
      {children}
    </label>
  );
}

function NativeSelect({ value, onChange, options }) {
  return (
    <select
      value={value}
      onChange={onChange}
      className="h-10 w-full rounded-card border border-border bg-surface px-3 text-sm text-text focus:outline-none focus:ring-2 focus:ring-accent/40"
    >
      <option value="">Select…</option>
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  );
}

function YesNoButtons({ value, onChange }) {
  return (
    <div className="flex gap-1.5">
      {['Yes', 'No'].map((opt) => (
        <Button
          key={opt}
          type="button"
          size="sm"
          variant={value === opt ? 'default' : 'outline'}
          onClick={() => onChange(value === opt ? '' : opt)}
        >
          {opt}
        </Button>
      ))}
    </div>
  );
}

// Bind-readiness strip: application-data completeness, never Policy_Number.
const READINESS_CHIPS = [
  ['blob_valid', 'Application data'],
  ['has_project', 'Project'],
  ['has_premium', 'Premium'],
  ['has_contact', 'Contact'],
  ['has_carrier', 'Carrier'],
  ['has_effective_date', 'Effective date'],
];

function ReadinessStrip({ readiness }) {
  if (!readiness) return null;
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {READINESS_CHIPS.map(([key, label]) => (
        <Badge key={key} variant={readiness[key] ? 'success' : 'warning'} className="text-[10px]">
          {label}
        </Badge>
      ))}
      <span className="ml-1 text-[11px] text-text-dim">
        {readiness.ready ? 'Ready to send for signature.' : 'Complete the amber items before sending for signature.'}
      </span>
    </div>
  );
}

export default function BrDataSheet({ dealId, open, onOpenChange, onSaved }) {
  const [form, setForm] = useState(null);
  const [dealMeta, setDealMeta] = useState(null);
  const [enums, setEnums] = useState(FALLBACK_ENUMS);
  const [readiness, setReadiness] = useState(null);
  const [parseError, setParseError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadErr, setLoadErr] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !dealId) return;
    let stale = false;
    setLoading(true); setLoadErr('');
    getBrData(dealId)
      .then((res) => {
        if (stale) return;
        if (!res?.success) throw new Error(res?.error || 'Load failed');
        setForm(seedForm(res));
        setDealMeta(res.deal || null);
        setEnums({ ...FALLBACK_ENUMS, ...(res.enums || {}) });
        setReadiness(res.readiness || null);
        setParseError(res.parse_error || null);
      })
      .catch((e) => { if (!stale) setLoadErr(e?.body?.error || e?.message || 'Load failed'); })
      .finally(() => { if (!stale) setLoading(false); });
    return () => { stale = true; };
  }, [open, dealId]);

  const set = (section, key) => (e) => {
    const value = e?.target ? e.target.value : e;
    setForm((f) => ({ ...f, [section]: { ...f[section], [key]: value } }));
  };

  function handleSave() {
    if (!dealId || saving || !form) return;
    setSaving(true);
    saveBrData(dealId, {
      data: formToBlob(form),
      policyNumber: form.terms.policy_number,
      proposedEffective: form.terms.proposed_effective,
      expirationDate: form.terms.expiration_date,
      amount: form.terms.amount === '' ? undefined : form.terms.amount,
    })
      .then((res) => {
        if (!res?.success) {
          toast.error(res?.errors?.length ? res.errors.join('; ') : (res?.error || 'Save failed'));
          return;
        }
        setReadiness(res.readiness || null);
        toast.success('Project data saved');
        for (const w of res.warnings || []) toast.info(w);
        onSaved?.(res);
        onOpenChange(false);
      })
      .catch((err) => {
        const body = err?.body;
        toast.error(body?.errors?.length ? body.errors.join('; ') : (body?.error || err?.message || 'Save failed'));
      })
      .finally(() => setSaving(false));
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="flex w-full flex-col gap-0 overflow-y-auto sm:max-w-2xl">
        <SheetHeader>
          <SheetTitle>Project &amp; terms</SheetTitle>
          <SheetDescription>
            The builders risk application data and the terms to collect. Everything here
            feeds the application the insured signs; nothing regenerates a signed document.
          </SheetDescription>
        </SheetHeader>

        <div className="border-b border-border py-2">
          <ReadinessStrip readiness={readiness} />
          {parseError ? (
            <p className="mt-1.5 text-[11px] text-danger">
              Stored data could not be parsed ({parseError}); you are editing a blank slate. Saving replaces it.
            </p>
          ) : null}
        </div>

        {loading || !form ? (
          <div className="flex flex-1 items-center justify-center py-16 text-sm text-text-muted">
            {loadErr || 'Loading project data…'}
          </div>
        ) : (
          <div className="flex-1 space-y-6 py-4">
            {/* Policy & terms */}
            <div className="space-y-2">
              <SectionTitle>Policy &amp; Terms</SectionTitle>
              <div className="grid grid-cols-2 gap-2">
                <Field label="Proposed effective date"><Input type="date" value={form.terms.proposed_effective} onChange={set('terms', 'proposed_effective')} /></Field>
                <Field label="Expiration date"><Input type="date" value={form.terms.expiration_date} onChange={set('terms', 'expiration_date')} /></Field>
                <Field label="Amount to collect ($)"><Input value={form.terms.amount} onChange={(e) => set('terms', 'amount')(e.target.value.replace(/[^0-9.]/g, ''))} placeholder="premium + TRIA" /></Field>
                <Field label="Policy number (assigned by carrier at binding)"><Input value={form.terms.policy_number} onChange={set('terms', 'policy_number')} placeholder="Backfill after the carrier binds" /></Field>
              </div>
              <p className="text-[11px] text-text-dim">
                Carrier: <span className="text-text">{dealMeta?.carrier || 'not set (set in Zoho)'}</span>
                <span className="mx-1.5 opacity-40">·</span>
                Contact: <span className="text-text">{dealMeta?.contact?.name || (dealMeta?.contact ? 'on file' : 'none on the Deal')}</span>
                <span className="mx-1.5 opacity-40">·</span>
                App #: <span className="text-text">{dealMeta?.application_number || 'assigned on create'}</span>
              </p>
            </div>

            {/* Applicant */}
            <div className="space-y-2">
              <SectionTitle>Applicant</SectionTitle>
              <div className="grid grid-cols-2 gap-2">
                <Field label="Entity type"><ListSelect field="entity_type" ariaLabel="Entity type" value={form.applicant.entity_type} onChange={set('applicant', 'entity_type')} /></Field>
                <Field label="Role on project"><NativeSelect value={form.applicant.role_on_project} onChange={set('applicant', 'role_on_project')} options={enums.role_on_project} /></Field>
                <Field label="Years experience"><Input value={form.applicant.years_experience} onChange={set('applicant', 'years_experience')} /></Field>
                <Field label="Contractor bonded"><YesNoButtons value={form.applicant.contractor_bonded} onChange={set('applicant', 'contractor_bonded')} /></Field>
                <Field label="Bonding company"><Input value={form.applicant.bonding_company} onChange={set('applicant', 'bonding_company')} /></Field>
                <Field label="FEIN"><Input value={form.applicant.fein} onChange={set('applicant', 'fein')} /></Field>
              </div>
            </div>

            {/* Project */}
            <div className="space-y-2">
              <SectionTitle>Project</SectionTitle>
              <Field label="Project description">
                <textarea
                  rows={2}
                  value={form.project.description}
                  onChange={set('project', 'description')}
                  className="w-full rounded-card border border-border bg-surface px-3 py-2 text-sm text-text focus:outline-none focus:ring-2 focus:ring-accent/40"
                />
              </Field>
              <div className="grid grid-cols-2 gap-2">
                <Field label="Jobsite street"><Input value={form.project.street} onChange={set('project', 'street')} /></Field>
                <Field label="Jobsite city"><Input value={form.project.city} onChange={set('project', 'city')} /></Field>
                <div className="grid grid-cols-2 gap-2">
                  <Field label="State"><Input maxLength={2} value={form.project.state} onChange={(e) => set('project', 'state')(e.target.value.toUpperCase())} /></Field>
                  <Field label="Zip"><Input value={form.project.zip} onChange={set('project', 'zip')} /></Field>
                </div>
                <Field label="Occupancy"><Input value={form.project.occupancy} onChange={set('project', 'occupancy')} placeholder="e.g. Aircraft hangar" /></Field>
                <Field label="Project type"><NativeSelect value={form.project.single_or_multi} onChange={set('project', 'single_or_multi')} options={enums.single_or_multi} /></Field>
                <Field label="Completed value ($)"><Input value={form.project.completed_value} onChange={(e) => set('project', 'completed_value')(e.target.value.replace(/[^0-9.]/g, ''))} /></Field>
                <Field label="Commencement date"><Input type="date" value={form.project.commencement_date} onChange={set('project', 'commencement_date')} /></Field>
                <Field label="Est. completion date"><Input type="date" value={form.project.est_completion_date} onChange={set('project', 'est_completion_date')} /></Field>
              </div>
            </div>

            {/* Construction */}
            <div className="space-y-2">
              <SectionTitle>Construction</SectionTitle>
              <div className="grid grid-cols-3 gap-2">
                <Field label="Foundation"><Input value={form.construction.foundation} onChange={set('construction', 'foundation')} /></Field>
                <Field label="Floors"><Input value={form.construction.floors} onChange={set('construction', 'floors')} /></Field>
                <Field label="Walls"><Input value={form.construction.walls} onChange={set('construction', 'walls')} /></Field>
                <Field label="Roof"><Input value={form.construction.roof} onChange={set('construction', 'roof')} /></Field>
                <Field label="Stories above grade"><Input value={form.construction.stories_above} onChange={set('construction', 'stories_above')} /></Field>
                <Field label="Stories below grade"><Input value={form.construction.stories_below} onChange={set('construction', 'stories_below')} /></Field>
                <Field label="Dimensions"><Input value={form.construction.dimensions} onChange={set('construction', 'dimensions')} /></Field>
                <Field label="Insulation"><Input value={form.construction.insulation} onChange={set('construction', 'insulation')} /></Field>
                <Field label="Percentage glass"><Input value={form.construction.percentage_glass} onChange={set('construction', 'percentage_glass')} /></Field>
              </div>
            </div>

            {/* Protection */}
            <div className="space-y-2">
              <SectionTitle>Protection</SectionTitle>
              <div className="grid grid-cols-3 gap-2">
                <Field label="Town protection class"><Input value={form.protection.town_protection_class} onChange={set('protection', 'town_protection_class')} /></Field>
                <Field label="Distance to hydrant"><Input value={form.protection.distance_to_hydrant} onChange={set('protection', 'distance_to_hydrant')} /></Field>
                <Field label="Distance to fire station"><Input value={form.protection.distance_to_fire_station} onChange={set('protection', 'distance_to_fire_station')} /></Field>
                <Field label="Fire department"><NativeSelect value={form.protection.fire_department_type} onChange={set('protection', 'fire_department_type')} options={enums.fire_department_types} /></Field>
                <Field label="Fenced and lighted"><YesNoButtons value={form.protection.fenced_lighted} onChange={set('protection', 'fenced_lighted')} /></Field>
                <Field label="Police protection"><YesNoButtons value={form.protection.police_protection} onChange={set('protection', 'police_protection')} /></Field>
                <Field label="Standpipes"><YesNoButtons value={form.protection.standpipes} onChange={set('protection', 'standpipes')} /></Field>
                <Field label="Watchman service"><Input value={form.protection.watchman_service} onChange={set('protection', 'watchman_service')} /></Field>
              </div>
            </div>

            {/* Coverage */}
            <div className="space-y-2">
              <SectionTitle>Coverage &amp; Premium</SectionTitle>
              <div className="grid grid-cols-2 gap-2">
                <Field label="Any one jobsite limit ($)"><Input value={form.coverage.any_jobsite_limit} onChange={(e) => set('coverage', 'any_jobsite_limit')(e.target.value.replace(/[^0-9.]/g, ''))} /></Field>
                <Field label="Any one jobsite deductible ($)"><Input value={form.coverage.any_jobsite_deductible} onChange={(e) => set('coverage', 'any_jobsite_deductible')(e.target.value.replace(/[^0-9.]/g, ''))} /></Field>
                <Field label="Transit limit ($)"><Input value={form.coverage.transit_limit} onChange={(e) => set('coverage', 'transit_limit')(e.target.value.replace(/[^0-9.]/g, ''))} /></Field>
                <Field label="Transit deductible ($)"><Input value={form.coverage.transit_deductible} onChange={(e) => set('coverage', 'transit_deductible')(e.target.value.replace(/[^0-9.]/g, ''))} /></Field>
                <Field label="Temp storage limit ($)"><Input value={form.coverage.temp_storage_limit} onChange={(e) => set('coverage', 'temp_storage_limit')(e.target.value.replace(/[^0-9.]/g, ''))} /></Field>
                <Field label="Temp storage deductible ($)"><Input value={form.coverage.temp_storage_deductible} onChange={(e) => set('coverage', 'temp_storage_deductible')(e.target.value.replace(/[^0-9.]/g, ''))} /></Field>
                <Field label="Any one loss limit ($)"><Input value={form.coverage.any_one_loss_limit} onChange={(e) => set('coverage', 'any_one_loss_limit')(e.target.value.replace(/[^0-9.]/g, ''))} /></Field>
                <Field label="Any one loss deductible ($)"><Input value={form.coverage.any_one_loss_deductible} onChange={(e) => set('coverage', 'any_one_loss_deductible')(e.target.value.replace(/[^0-9.]/g, ''))} /></Field>
                <Field label="Earthquake limit ($)"><Input value={form.coverage.earthquake_limit} onChange={(e) => set('coverage', 'earthquake_limit')(e.target.value.replace(/[^0-9.]/g, ''))} /></Field>
                <Field label="Flood limit ($)"><Input value={form.coverage.flood_limit} onChange={(e) => set('coverage', 'flood_limit')(e.target.value.replace(/[^0-9.]/g, ''))} /></Field>
                <Field label="Windstorm / hail deductible"><Input value={form.coverage.windstorm_deductible} onChange={set('coverage', 'windstorm_deductible')} placeholder="e.g. 2% or $5,000" /></Field>
                <Field label="Coinsurance"><Input value={form.coverage.coinsurance} onChange={set('coverage', 'coinsurance')} placeholder="e.g. 100%" /></Field>
                <Field label="Permission to occupy"><NativeSelect value={form.coverage.permission_to_occupy} onChange={set('coverage', 'permission_to_occupy')} options={enums.permission_to_occupy} /></Field>
                <Field label="TRIPRA accepted"><YesNoButtons value={form.coverage.tripra_accepted} onChange={set('coverage', 'tripra_accepted')} /></Field>
                <Field label="Annual premium ($)"><Input value={form.coverage.annual_premium} onChange={(e) => set('coverage', 'annual_premium')(e.target.value.replace(/[^0-9.]/g, ''))} /></Field>
                <Field label="TRIPRA premium ($)"><Input value={form.coverage.tripra_premium} onChange={(e) => set('coverage', 'tripra_premium')(e.target.value.replace(/[^0-9.]/g, ''))} /></Field>
              </div>
            </div>

            {/* Transit / storage / rigging */}
            <div className="space-y-2">
              <SectionTitle>Transit, Storage &amp; Rigging</SectionTitle>
              <div className="grid grid-cols-2 gap-2">
                <Field label="Property in transit"><Input value={form.transit_storage_rigging.transit_value} onChange={set('transit_storage_rigging', 'transit_value')} /></Field>
                <Field label="Off-site temp storage"><Input value={form.transit_storage_rigging.off_site_temp_storage} onChange={set('transit_storage_rigging', 'off_site_temp_storage')} /></Field>
                <Field label="Rigging / hoisting"><Input value={form.transit_storage_rigging.rigging_hoisting} onChange={set('transit_storage_rigging', 'rigging_hoisting')} /></Field>
                <Field label="Scaffolding"><NativeSelect value={form.transit_storage_rigging.scaffolding} onChange={set('transit_storage_rigging', 'scaffolding')} options={enums.scaffolding_types} /></Field>
              </div>
            </div>

            {/* Loss history */}
            <div className="space-y-2">
              <SectionTitle>Loss History</SectionTitle>
              <Field label="Claims summary (5 yr)">
                <textarea
                  rows={2}
                  value={form.loss_history.claims_summary}
                  onChange={set('loss_history', 'claims_summary')}
                  className="w-full rounded-card border border-border bg-surface px-3 py-2 text-sm text-text focus:outline-none focus:ring-2 focus:ring-accent/40"
                />
              </Field>
              <Field label="Current / prior insurance">
                <textarea
                  rows={2}
                  value={form.loss_history.current_prior_insurance}
                  onChange={set('loss_history', 'current_prior_insurance')}
                  className="w-full rounded-card border border-border bg-surface px-3 py-2 text-sm text-text focus:outline-none focus:ring-2 focus:ring-accent/40"
                />
              </Field>
            </div>
          </div>
        )}

        <div className="flex items-center justify-end gap-2 border-t border-border pt-3">
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving || loading || !form}>
            {saving ? (
              <>
                <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                Saving…
              </>
            ) : (
              'Save'
            )}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
